import * as React from 'react';
import {
  Alert,
  Box,
  Button,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  Divider,
  FormControlLabel,
  Paper,
  Skeleton,
  Stack,
  Switch,
  Tab,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Tabs,
  Typography,
} from '@mui/material';
import { LoadingButton } from '@mui/lab';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { toast } from '@/components/core/v2/toaster';
import dayjs from 'dayjs';

import {
  completeStripeConnectCallback,
  disconnectStripeConnect,
  getCommunityTransactions,
  getStripeConnectStatus,
  startStripeConnectOnboarding,
  updateCommunityPreferences,
  type Transaction,
} from '@/api/communities';
import { useUser } from '@/hooks/v2/use-user';
import { useSearchParams } from 'react-router-dom';

function TabPanel({
  children,
  value,
  index,
}: {
  children?: React.ReactNode;
  value: number;
  index: number;
}): React.JSX.Element {
  return (
    <Box
      role="tabpanel"
      hidden={value !== index}
      id={`payments-tabpanel-${index}`}
      aria-labelledby={`payments-tab-${index}`}
    >
      {value === index && children}
    </Box>
  );
}

export default function CommunityPaymentsPage(): React.JSX.Element | null {
  const { community } = useUser();
  const [currentTab, setCurrentTab] = React.useState(0);
  const [searchParams, setSearchParams] = useSearchParams();
  const queryClient = useQueryClient();
  const [callbackProcessing, setCallbackProcessing] = React.useState(false);
  const callbackHandled = React.useRef(false);

  // Handle Stripe OAuth callback — pick up ?code=...&state=... from the redirect
  React.useEffect(() => {
    const code = searchParams.get('code');
    const state = searchParams.get('state');
    if (!code || !community?.id || callbackHandled.current) return;

    // Mark as handled immediately so double-renders / strict mode don't re-fire
    callbackHandled.current = true;
    setCallbackProcessing(true);
    setSearchParams({}, { replace: true });

    const communityId = state || community.id;

    completeStripeConnectCallback(String(communityId), code)
      .then(() => {
        toast.success('Stripe account connected successfully!');
        queryClient.invalidateQueries({ queryKey: ['stripe-connect-status', community.id] });
      })
      .catch((err: any) => {
        toast.error(err?.response?.data?.message ?? 'Failed to complete Stripe connection.');
      })
      .finally(() => setCallbackProcessing(false));
  }, [searchParams, community?.id]);

  if (!community) return null;

  const paymentsEnabled = community.payments_enabled ?? false;

  return (
    <Box sx={{ width: '100%', maxWidth: 860 }}>
      {callbackProcessing && (
        <Alert severity="info" sx={{ mb: 3 }}>
          Connecting your Stripe account…
        </Alert>
      )}

      {!paymentsEnabled && (
        <Alert severity="warning" sx={{ mb: 3 }}>
          Payments have not been enabled for this community. Please contact your account administrator to
          enable payments before connecting Stripe or collecting deposits.
        </Alert>
      )}

      <Paper
        elevation={0}
        sx={{
          p: 2.5,
          mb: 3,
          border: '1px solid',
          borderColor: 'divider',
          borderRadius: 2,
          bgcolor: 'action.hover',
        }}
      >
        <Typography variant="body1" color="text.secondary" sx={{ lineHeight: 1.6 }}>
          Manage your community&apos;s Stripe account for deposit collection and view payment history.
        </Typography>
      </Paper>

      <Paper
        elevation={0}
        sx={{
          border: '1px solid',
          borderColor: 'divider',
          borderRadius: 2,
          overflow: 'hidden',
          opacity: paymentsEnabled ? 1 : 0.5,
          pointerEvents: paymentsEnabled ? 'auto' : 'none',
        }}
      >
        <Tabs
          value={currentTab}
          onChange={(_, v) => setCurrentTab(v)}
          sx={{ px: 3, borderBottom: '1px solid', borderColor: 'divider' }}
        >
          <Tab label="Stripe Setup" id="payments-tab-0" aria-controls="payments-tabpanel-0" />
          <Tab label="Transactions" id="payments-tab-1" aria-controls="payments-tabpanel-1" />
        </Tabs>

        <TabPanel value={currentTab} index={0}>
          <StripeSetupTab communityId={community.id} callbackProcessing={callbackProcessing} />
        </TabPanel>
        <TabPanel value={currentTab} index={1}>
          <TransactionsTab communityId={community.id} />
        </TabPanel>
      </Paper>
    </Box>
  );
}

function StripeSetupTab({ communityId, callbackProcessing }: { communityId: string; callbackProcessing: boolean }): React.JSX.Element {
  const queryClient = useQueryClient();
  const { community } = useUser();
  const [confirmDisableOpen, setConfirmDisableOpen] = React.useState(false);

  const { data: connectStatus, isLoading } = useQuery({
    queryKey: ['stripe-connect-status', communityId],
    queryFn: () => getStripeConnectStatus(communityId),
    // Poll every 5s while connecting so the UI reflects Stripe's async review
    refetchInterval: (query) => {
      const data = query.state.data;
      if (data?.connected && !data?.charges_enabled) return 5000;
      return false;
    },
  });

  const onboardMutation = useMutation({
    mutationFn: () => startStripeConnectOnboarding(communityId),
    onSuccess: (data) => {
      window.location.href = data.url;
    },
    onError: (error: any) => {
      toast.error(error?.response?.data?.message ?? 'Failed to start Stripe onboarding.');
    },
  });

  const disconnectMutation = useMutation({
    mutationFn: () => disconnectStripeConnect(communityId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['stripe-connect-status', communityId] });
      toast.success('Stripe account disconnected.');
    },
    onError: (error: any) => {
      toast.error(error?.response?.data?.message ?? 'Failed to disconnect Stripe account.');
    },
  });

  const enableDepositsMutation = useMutation({
    mutationFn: (enabled: boolean) =>
      updateCommunityPreferences(communityId, { payments_enabled: enabled }),
    onSuccess: (_, enabled) => {
      queryClient.invalidateQueries({ queryKey: ['stripe-connect-status', communityId] });
      queryClient.invalidateQueries({ queryKey: ['community', communityId] });
      toast.success(enabled ? 'Deposits enabled for residents.' : 'Deposits disabled for residents.');
      setConfirmDisableOpen(false);
    },
    onError: (error: any) => {
      toast.error(error?.response?.data?.message ?? 'Failed to update deposit setting.');
    },
  });

  const handleToggleDeposits = (e: React.ChangeEvent<HTMLInputElement>) => {
    const newValue = e.target.checked;
    // If toggling ON, just enable it. If toggling OFF, show confirmation.
    if (newValue) {
      enableDepositsMutation.mutate(true);
    } else {
      setConfirmDisableOpen(true);
    }
  };

  const handleConfirmDisable = () => {
    enableDepositsMutation.mutate(false);
  };

  const isConnected = connectStatus?.connected;
  const chargesEnabled = connectStatus?.charges_enabled;
  const payoutsEnabled = connectStatus?.payouts_enabled;
  const detailsSubmitted = connectStatus?.onboarding_complete;
  const isFullyReady = chargesEnabled && detailsSubmitted;
  const depositsEnabled = connectStatus?.payments_enabled ?? community?.payments_enabled ?? false;

  // Button is busy if we are mid-redirect, returning from Stripe callback, or if Stripe is still processing
  const connectBusy = onboardMutation.isPending || callbackProcessing;

  const getStatusChip = () => {
    if (isFullyReady) {
      return <Chip label="Connected & active" color="success" size="small" />;
    }
    if (isConnected && detailsSubmitted && !chargesEnabled) {
      return <Chip label="Pending review" color="info" size="small" />;
    }
    if (isConnected) {
      return <Chip label="Setup incomplete" color="warning" size="small" />;
    }
    return null;
  };

  return (
    <Box sx={{ p: 3 }}>
      <Typography variant="subtitle1" fontWeight={600} sx={{ mb: 0.5 }}>
        Stripe Connect
      </Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
        Connect a Stripe account so reservation deposits and payments go directly into your community&apos;s bank
        account. Each community uses its own Stripe account — funds are never pooled through a platform account.
      </Typography>

      {isLoading ? (
        <Stack spacing={2}>
          <Skeleton variant="rounded" height={32} width={200} />
          <Skeleton variant="rounded" height={20} width={340} />
        </Stack>
      ) : isConnected ? (
        <Stack spacing={2.5}>
          <Stack direction="row" spacing={1.5} sx={{ alignItems: 'center', flexWrap: 'wrap', gap: 1 }}>
            {getStatusChip()}
            {payoutsEnabled && (
              <Chip label="Payouts enabled" color="success" size="small" variant="outlined" />
            )}
          </Stack>

          {!isFullyReady && (
            <Alert severity={detailsSubmitted ? 'info' : 'warning'}>
              {detailsSubmitted && !chargesEnabled
                ? 'Your Stripe account is connected and details have been submitted. Stripe may still be reviewing your account — this can take a few minutes in test mode. Check your Stripe Dashboard for any outstanding requirements or try refreshing this page.'
                : 'Stripe setup is not complete. Residents cannot make deposits until charges are enabled on your Stripe account. Complete the onboarding process on Stripe to activate charges.'}
            </Alert>
          )}

          {/* Enable deposits for residents — only shown once Stripe is fully ready */}
          {isFullyReady && (
            <>
              <Divider />
              <Box>
                <Typography variant="subtitle2" fontWeight={600} sx={{ mb: 0.5 }}>
                  Resident deposits
                </Typography>
                <Typography variant="body2" color="text.secondary" sx={{ mb: 1.5 }}>
                  Once enabled, residents can add payment methods and pay deposits when booking amenities.
                  Disable this if you need to configure amenities or run internal tests before going live.
                </Typography>
                <FormControlLabel
                  control={
                    <Switch
                      checked={depositsEnabled}
                      onChange={handleToggleDeposits}
                      disabled={enableDepositsMutation.isPending}
                    />
                  }
                  label={
                    <Typography variant="body2" fontWeight={500}>
                      {depositsEnabled ? 'Deposits enabled for residents' : 'Deposits disabled for residents'}
                    </Typography>
                  }
                />
              </Box>
            </>
          )}

          <Divider />

          <Box>
            {connectStatus?.business_name && (
              <Typography variant="body2" color="text.secondary" sx={{ mb: 0.5 }}>
                Business: <strong>{connectStatus.business_name}</strong>
              </Typography>
            )}
            {connectStatus?.email && (
              <Typography variant="body2" color="text.secondary" sx={{ mb: 0.5 }}>
                Email: <strong>{connectStatus.email}</strong>
              </Typography>
            )}
            {connectStatus?.bank_last4 && (
              <Typography variant="body2" color="text.secondary" sx={{ mb: 0.5 }}>
                Bank account: <strong>{connectStatus.bank_name ? `${connectStatus.bank_name} ` : ''}····{connectStatus.bank_last4}</strong>
              </Typography>
            )}
            <Typography variant="body2" color="text.secondary" sx={{ mb: 0.5 }}>
              Account ID: <strong>{connectStatus?.account_id}</strong>
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 0.5 }}>
              Details submitted: <strong>{detailsSubmitted ? 'Yes' : 'No'}</strong>
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 0.5 }}>
              Charges enabled: <strong>{chargesEnabled ? 'Yes' : 'No'}</strong>
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 1.5 }}>
              Payouts enabled: <strong>{payoutsEnabled ? 'Yes' : 'No'}</strong>
            </Typography>
          </Box>

          <Stack direction="row" spacing={1} sx={{ flexWrap: 'wrap', gap: 1 }}>
            <Button
              variant="outlined"
              href={`https://dashboard.stripe.com/${connectStatus?.account_id}`}
              target="_blank"
              rel="noopener noreferrer"
            >
              Open Stripe Dashboard
            </Button>
            {!isFullyReady && (
              <LoadingButton
                variant="contained"
                loading={connectBusy}
                disabled={connectBusy}
                onClick={() => onboardMutation.mutate()}
              >
                Re-authorize Stripe account
              </LoadingButton>
            )}
            <Button
              variant="outlined"
              color="error"
              onClick={() => {
                if (
                  window.confirm(
                    'Are you sure you want to disconnect your Stripe account? Residents will not be able to make payments until you reconnect.'
                  )
                ) {
                  disconnectMutation.mutate();
                }
              }}
              disabled={disconnectMutation.isPending}
            >
              Disconnect Stripe
            </Button>
          </Stack>
        </Stack>
      ) : (
        <Stack spacing={2}>
          <Alert severity="info">
            No Stripe account connected. Connect an existing Stripe account or create a new one to start accepting
            deposits from residents.
          </Alert>
          <LoadingButton
            variant="contained"
            loading={connectBusy}
            disabled={connectBusy}
            onClick={() => onboardMutation.mutate()}
            sx={{ alignSelf: 'flex-start' }}
          >
            {connectBusy ? 'Redirecting to Stripe…' : 'Connect Stripe account'}
          </LoadingButton>
        </Stack>
      )}

      {/* Confirmation dialog for disabling deposits */}
      <Dialog open={confirmDisableOpen} onClose={() => setConfirmDisableOpen(false)}>
        <DialogTitle>Disable deposits for residents?</DialogTitle>
        <DialogContent>
          <DialogContentText>
            If you disable deposits, residents will not be able to add payment methods or pay deposits for amenities.
            This action can be reversed at any time.
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button
            color="inherit"
            onClick={() => setConfirmDisableOpen(false)}
            disabled={enableDepositsMutation.isPending}
          >
            Cancel
          </Button>
          <LoadingButton
            color="error"
            variant="contained"
            loading={enableDepositsMutation.isPending}
            onClick={handleConfirmDisable}
          >
            Disable
          </LoadingButton>
        </DialogActions>
      </Dialog>
    </Box>
  );
}

const txTypeLabels: Record<string, string> = {
  charge: 'Charge',
  refund: 'Refund',
  payout: 'Payout',
  transfer: 'Transfer',
  adjustment: 'Adjustment',
  stripe_fee: 'Stripe Fee',
  payment: 'Payment',
  payment_refund: 'Refund',
};

const txStatusColors: Record<string, 'success' | 'warning' | 'default' | 'error'> = {
  available: 'success',
  pending: 'warning',
  failed: 'error',
};

function TransactionsTab({ communityId }: { communityId: string }): React.JSX.Element {
  const [startingAfter, setStartingAfter] = React.useState<string | undefined>(undefined);
  const [history, setHistory] = React.useState<string[]>([]);

  const { data, isLoading, isError } = useQuery({
    queryKey: ['community-transactions', communityId, startingAfter],
    queryFn: () => getCommunityTransactions(communityId, { limit: 25, starting_after: startingAfter }),
    refetchOnWindowFocus: false,
  });

  const transactions: Transaction[] = data?.transactions ?? [];

  const handleNextPage = () => {
    if (data?.last_id) {
      setHistory((prev) => [...prev, data.first_id ?? '']);
      setStartingAfter(data.last_id ?? undefined);
    }
  };

  const handlePrevPage = () => {
    const prev = [...history];
    prev.pop();
    setHistory(prev);
    setStartingAfter(prev.length > 0 ? prev[prev.length - 1] : undefined);
  };

  if (isLoading) {
    return (
      <Box sx={{ p: 3 }}>
        <Stack spacing={1}>
          {Array.from({ length: 8 }).map((_, i) => (
            <Skeleton key={i} variant="rounded" height={40} />
          ))}
        </Stack>
      </Box>
    );
  }

  if (isError) {
    return (
      <Box sx={{ p: 3 }}>
        <Alert severity="error">Failed to load transactions. Make sure Stripe Connect is configured.</Alert>
      </Box>
    );
  }

  if (data?.message && transactions.length === 0) {
    return (
      <Box sx={{ p: 3 }}>
        <Alert severity="info">{data.message}</Alert>
      </Box>
    );
  }

  return (
    <Box>
      <Box sx={{ overflowX: 'auto' }}>
        <Table size="small" sx={{ minWidth: 640 }}>
          <TableHead>
            <TableRow sx={{ bgcolor: 'action.hover' }}>
              <TableCell sx={{ fontWeight: 600, fontSize: '0.75rem', color: 'text.secondary', py: 1.5 }}>Date</TableCell>
              <TableCell sx={{ fontWeight: 600, fontSize: '0.75rem', color: 'text.secondary', py: 1.5 }}>Type</TableCell>
              <TableCell sx={{ fontWeight: 600, fontSize: '0.75rem', color: 'text.secondary', py: 1.5 }}>Description</TableCell>
              <TableCell sx={{ fontWeight: 600, fontSize: '0.75rem', color: 'text.secondary', py: 1.5 }} align="right">Amount</TableCell>
              <TableCell sx={{ fontWeight: 600, fontSize: '0.75rem', color: 'text.secondary', py: 1.5 }} align="right">Fee</TableCell>
              <TableCell sx={{ fontWeight: 600, fontSize: '0.75rem', color: 'text.secondary', py: 1.5 }} align="right">Net</TableCell>
              <TableCell sx={{ fontWeight: 600, fontSize: '0.75rem', color: 'text.secondary', py: 1.5 }}>Status</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {transactions.length === 0 ? (
              <TableRow>
                <TableCell colSpan={7} align="center" sx={{ py: 4, color: 'text.secondary' }}>
                  No transactions found.
                </TableCell>
              </TableRow>
            ) : (
              transactions.map((txn) => (
                <TableRow key={txn.id} hover>
                  <TableCell sx={{ py: 1.5, fontSize: '0.8rem', color: 'text.secondary', whiteSpace: 'nowrap' }}>
                    {dayjs.unix(txn.created).format('MMM D, YYYY')}
                  </TableCell>
                  <TableCell sx={{ py: 1.5 }}>
                    <Typography variant="body2" fontSize="0.8rem">
                      {txTypeLabels[txn.type] ?? txn.type}
                    </Typography>
                  </TableCell>
                  <TableCell sx={{ py: 1.5, maxWidth: 260 }}>
                    <Typography variant="body2" fontSize="0.8rem" noWrap title={txn.description ?? ''}>
                      {txn.description ?? '—'}
                    </Typography>
                  </TableCell>
                  <TableCell align="right" sx={{ py: 1.5 }}>
                    <Typography
                      variant="body2"
                      fontSize="0.8rem"
                      fontWeight={500}
                      color={txn.amount < 0 ? 'error.main' : 'text.primary'}
                    >
                      {txn.amount < 0 ? '-' : ''}${Math.abs(txn.amount).toFixed(2)}
                    </Typography>
                  </TableCell>
                  <TableCell align="right" sx={{ py: 1.5 }}>
                    <Typography variant="body2" fontSize="0.8rem" color="text.secondary">
                      ${txn.fee.toFixed(2)}
                    </Typography>
                  </TableCell>
                  <TableCell align="right" sx={{ py: 1.5 }}>
                    <Typography
                      variant="body2"
                      fontSize="0.8rem"
                      fontWeight={600}
                      color={txn.net < 0 ? 'error.main' : 'success.main'}
                    >
                      {txn.net < 0 ? '-' : ''}${Math.abs(txn.net).toFixed(2)}
                    </Typography>
                  </TableCell>
                  <TableCell sx={{ py: 1.5 }}>
                    <Chip
                      label={txn.status.charAt(0).toUpperCase() + txn.status.slice(1)}
                      size="small"
                      color={txStatusColors[txn.status] ?? 'default'}
                      sx={{ fontSize: '0.7rem' }}
                    />
                  </TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      </Box>

      {(history.length > 0 || data?.has_more) && (
        <Stack direction="row" justifyContent="flex-end" spacing={1} sx={{ p: 2, borderTop: '1px solid', borderColor: 'divider' }}>
          <Button size="small" variant="outlined" onClick={handlePrevPage} disabled={history.length === 0}>
            Previous
          </Button>
          <Button size="small" variant="outlined" onClick={handleNextPage} disabled={!data?.has_more}>
            Next
          </Button>
        </Stack>
      )}
    </Box>
  );
}
